import type { Lang, PersonName, PersonWithNames } from './types';

/** Preferred name record in the requested language, falling back to the other language. Never machine-translates. */
export function nameFor(person: { names: PersonName[] }, lang: Lang): PersonName | undefined {
  const inLang = person.names.filter((n) => n.language === lang);
  const preferred = inLang.find((n) => n.preferred) ?? inLang[0];
  if (preferred) return preferred;
  const other = person.names.filter((n) => n.language !== lang);
  return other.find((n) => n.preferred) ?? other[0];
}

export function displayName(person: { names: PersonName[] }, lang: Lang): string {
  const n = nameFor(person, lang);
  if (!n) return lang === 'he' ? 'ללא שם' : 'Unnamed';
  return n.display?.trim() || [n.given, n.family].filter(Boolean).join(' ').trim() || (lang === 'he' ? 'ללא שם' : 'Unnamed');
}

export function givenName(person: { names: PersonName[] }, lang: Lang): string {
  const n = nameFor(person, lang);
  return n?.nickname || n?.given || displayName(person, lang);
}

/** Whether the name we are about to render is in a different script than the UI (used for bidi isolation styling). */
export function nameLang(person: { names: PersonName[] }, lang: Lang): Lang {
  return nameFor(person, lang)?.language ?? lang;
}

export function initials(person: { names: PersonName[] }, lang: Lang): string {
  const n = nameFor(person, lang);
  if (!n) return '?';
  const a = (n.given || '').trim().charAt(0);
  const b = (n.family || '').trim().charAt(0);
  return (a + b).toUpperCase() || '?';
}

/** All searchable strings for a person across both languages, nicknames and previous names. */
export function searchTerms(person: PersonWithNames): string[] {
  const out: string[] = [];
  for (const n of person.names) {
    out.push(n.given, n.family, n.display ?? '', n.nickname ?? '', n.middle ?? '', `${n.given} ${n.family}`);
    if (n.previousNames) out.push(...n.previousNames);
  }
  return out.filter(Boolean).map((s) => s.toLowerCase());
}

export function normalize(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-֑ͯ-ׇ]/g, '') // latin accents + hebrew niqqud
    .replace(/[^\p{L}\p{N}\s'-]/gu, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/*
 * A string made ready to be compared: tidied, and cut into its pairs of letters.
 *
 * Finding somebody compares what was typed with every name of every person in
 * the family — and used to tidy and cut up *both* strings afresh for every one
 * of those comparisons, so the four letters somebody had typed were normalised
 * eight thousand times a keystroke, and so was every name, again on the next
 * keystroke. A name does not change between keystrokes. Each distinct string is
 * prepared once and kept; the answer is exactly what it always was.
 */
interface Prepared {
  text: string;
  grams: Map<string, number>;
  total: number;
}
const prepared = new Map<string, Prepared>();
/** Enough for a very large family's every name in both languages; past it the lot is let go and refilled as needed. */
const PREPARED_MAX = 40_000;

function prepare(s: string): Prepared {
  let p = prepared.get(s);
  if (p) return p;
  const text = normalize(s);
  const grams = new Map<string, number>();
  let total = 0;
  if (text) {
    const t = ` ${text} `;
    for (let i = 0; i < t.length - 1; i++) {
      const k = t.slice(i, i + 2);
      grams.set(k, (grams.get(k) ?? 0) + 1);
      total++;
    }
  }
  p = { text, grams, total };
  if (prepared.size >= PREPARED_MAX) prepared.clear();
  prepared.set(s, p);
  return p;
}

/** 0..1 similarity used for duplicate warnings. Small, dependency-free (bigram Dice coefficient). */
export function similarity(a: string, b: string): number {
  const A = prepare(a);
  const B = prepare(b);
  if (!A.text || !B.text) return 0;
  if (A.text === B.text) return 1;
  let inter = 0;
  for (const [k, v] of A.grams) inter += Math.min(v, B.grams.get(k) ?? 0);
  const total = A.total + B.total;
  return total === 0 ? 0 : (2 * inter) / total;
}
